import React, { createContext, FC, useCallback, useState } from 'react';

import { fetchMerchantPaymentConfig } from 'services/cos-service/ecomm-payment-config';
import { MerchantPaymentConfig } from 'services/cos-service/models';
import { useSafeContext } from 'hooks/useSafeContext/useSafeContext';

export type MerchantPaymentConfigContextType = {
  pending: boolean;
  error?: string;
  merchantPaymentConfig?: MerchantPaymentConfig;
  getMerchantPaymentConfig: (merchantId: Uuid) => void;
}

const MerchantPaymentConfigContext = createContext<MerchantPaymentConfigContextType | undefined>(undefined);
// eslint-disable-next-line
MerchantPaymentConfigContext.displayName = 'MerchantPaymentConfig';

export const MerchantPaymentConfigContextProvider: FC = ({ children }) => {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [merchantPaymentConfig, setMerchantPaymentConfig] = useState<MerchantPaymentConfig | undefined>(undefined);

  const getMerchantPaymentConfig = useCallback(async (merchantId: Uuid) => {
    setPending(true);
    try {
      const sessionPaymentConfig = sessionStorage.getItem(`${merchantId}:ecomPaymentConfig`);
      if (sessionPaymentConfig) {
        return setMerchantPaymentConfig(JSON.parse(sessionPaymentConfig) as MerchantPaymentConfig);
      }

      const paymentConfig = await fetchMerchantPaymentConfig(merchantId);
      setMerchantPaymentConfig(paymentConfig);
      sessionStorage.setItem(`${merchantId}:ecomPaymentConfig`, JSON.stringify(paymentConfig));
    } catch ({ response, message = '' }) {
      setError(response?.data?.message ?? message);
    } finally {
      setPending(false);
    }
  }, [setPending, setError, setMerchantPaymentConfig]);

  const context = {
    pending,
    error,
    merchantPaymentConfig,
    getMerchantPaymentConfig,
  };

  return (
    <MerchantPaymentConfigContext.Provider value={context}>
      {children}
    </MerchantPaymentConfigContext.Provider>
  );
};

export const useMerchantPaymentConfigContext = () => {
  return useSafeContext(useMerchantPaymentConfigContext, MerchantPaymentConfigContext);
};
